import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { StepObjectVisibility, TimelineStep } from '../types/timeline';

interface StepVisibilityOptions {
  mode?: 'playback' | 'editing';
}

function sortSteps(steps: TimelineStep[]): TimelineStep[] {
  return [...steps].sort((a, b) => a.order - b.order);
}

function isEligibleVisibilityNode(object: THREE.Object3D): boolean {
  if (!object.parent) return false;
  if ((object as THREE.Camera).isCamera) return false;
  if ((object as THREE.Light).isLight) return false;
  if (object.type === 'Bone') return false;
  return true;
}

function getObjectVisibilityKey(object: THREE.Object3D): string {
  return object.name || object.uuid;
}

function buildVisibilityAssignmentLookup(
  orderedSteps: TimelineStep[],
  currentStepIndex: number,
  assignments: StepObjectVisibility[],
): Map<string, boolean> {
  if (currentStepIndex < 0) return new Map();

  // Only apply assignments for the CURRENT step. Objects without an explicit
  // assignment at this step default to visible (the scene traverse uses
  // `lookup.has(key) ? lookup.get(key) : true`). This prevents "hidden in
  // step 1" from cascading forward into all subsequent steps.
  const currentStepId = orderedSteps[currentStepIndex]?.id;
  if (!currentStepId) return new Map();

  const lookup = new Map<string, boolean>();
  for (const assignment of assignments) {
    if (assignment.stepId !== currentStepId) continue;
    lookup.set(assignment.objectId, assignment.included);
  }

  return lookup;
}

export function getCurrentVisibilityStepIndex(steps: TimelineStep[], frame: number): number {
  if (steps.length === 0) return -1;

  const orderedSteps = sortSteps(steps);
  if (frame <= orderedSteps[0].startFrame) return 0;
  if (frame >= orderedSteps[orderedSteps.length - 1].endFrame) return orderedSteps.length - 1;

  for (let index = 0; index < orderedSteps.length; index += 1) {
    const step = orderedSteps[index];
    if (frame >= step.startFrame && frame <= step.endFrame) {
      return index;
    }
  }

  return orderedSteps.length - 1;
}

export function applyStepVisibilityToScene(
  scene: THREE.Object3D | null,
  steps: TimelineStep[],
  currentFrame: number,
  assignments: StepObjectVisibility[],
  options: StepVisibilityOptions = {},
): void {
  if (!scene) return;

  if (options.mode === 'editing') {
    scene.traverse((object) => {
      if (!isEligibleVisibilityNode(object)) return;
      object.visible = true;
    });
    return;
  }

  const orderedSteps = sortSteps(steps);
  const currentStepIndex = getCurrentVisibilityStepIndex(orderedSteps, currentFrame);
  const visibilityLookup = buildVisibilityAssignmentLookup(orderedSteps, currentStepIndex, assignments);

  scene.traverse((object) => {
    if (!isEligibleVisibilityNode(object)) return;

    const objectKey = getObjectVisibilityKey(object);
    const nextVisible = visibilityLookup.has(objectKey) ? visibilityLookup.get(objectKey)! : true;
    object.visible = nextVisible;
  });
}

interface UseStepVisibilityControllerOptions {
  scene: THREE.Object3D | null;
  steps: TimelineStep[];
  currentFrame: number;
  assignments: StepObjectVisibility[];
  mode?: 'playback' | 'editing';
}

export function useStepVisibilityController({
  scene,
  steps,
  currentFrame,
  assignments,
  mode = 'playback',
}: UseStepVisibilityControllerOptions) {
  const orderedSteps = useMemo(() => sortSteps(steps), [steps]);

  useEffect(() => {
    applyStepVisibilityToScene(scene, orderedSteps, currentFrame, assignments, { mode });
  }, [assignments, currentFrame, mode, orderedSteps, scene]);
}
